import {getCurrentUserLogin} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {IssueFieldSelection, IssueFieldSelectionValues} from '@github-ui/issue-fields-shared/issue-field'
import {
  asIssueFieldSelection,
  getIssueFieldValue,
  parsePinnedIssueFields,
  toIssueFieldType,
} from '@github-ui/issue-fields-shared/issue-field'
import {useIssueFieldsAnalytics} from '@github-ui/issue-fields-shared/use-issue-fields-analytics'
import type {IssueFieldWithUnsavedValue} from '@github-ui/issue-metadata-types/issue-field-with-unsaved-value'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Heading, Link, Tooltip} from '@primer/react'
import {useCallback, useEffect, useEffectEvent, useMemo, useState} from 'react'

import {
  areFieldValuesEqual,
  createClearedFieldValue,
  getFieldChanges,
  getIssueFieldSelectionValue,
  toIssueFieldWithUnsavedValue,
} from '../../utils/issue-field-helper'
import type {IssueNode, IssueNodeRepository} from '../issue-viewer-view-query-types'
import {Section} from '../Section'
import {LABELS, shouldShowFieldVisibilityIndicators} from './constants/labels'
import styles from './FieldsSection.module.css'
import {getPinnedFieldClassName, getPinnedFieldPosition, runFieldMutation, usePinnedFieldsAnimation} from './helpers'
import {ErrorDialog, getIssueFieldEditor, type SafeFieldValue} from './IssueFieldEditors'
import {useDeleteIssueFieldValueMutation} from './mutations/delete-issue-field-value-mutation'
import {toOptimisticIssueFieldValue} from './mutations/helpers'
import {useIssueFieldValueMutation} from './mutations/update-issue-field-value-mutation'

type FieldsSectionIssueFieldsOwner = Extract<
  NonNullable<IssueNodeRepository['owner']>,
  {readonly __typename: 'Organization' | 'User'}
>
type FieldsSectionRepositoryOwner = Pick<
  NonNullable<IssueNodeRepository['owner']>,
  '__typename' | 'databaseId' | 'issueFieldsEnabled' | 'login'
> &
  Partial<Pick<FieldsSectionIssueFieldsOwner, 'issueFields' | 'pinnedIssueFields'>>
type FieldsSectionRepository = Pick<IssueNodeRepository, 'isOwnerEnterpriseManaged' | 'visibility'> & {
  readonly owner: FieldsSectionRepositoryOwner
}

export type EditIssueFieldsSectionFragment = Pick<
  IssueNode,
  'id' | 'number' | 'issueFieldValues' | 'issueType' | 'viewerCanSetFields'
> & {
  readonly repository: FieldsSectionRepository & Pick<IssueNodeRepository, 'name'>
}

export type FieldsSectionFieldValuesFragment = Pick<
  EditIssueFieldsSectionFragment,
  'id' | 'number' | 'issueFieldValues' | 'viewerCanSetFields' | 'issueType' | 'repository'
>

type RelayIssueFieldDataType = NonNullable<SafeFieldValue['field']['dataType']>
// Relay fragment enums are open and may include values this UI doesn't support yet.
// Guard on known values before committing mutations.
const SUPPORTED_ISSUE_FIELD_DATA_TYPES = new Set<RelayIssueFieldDataType>([
  'TEXT',
  'SINGLE_SELECT',
  'MULTI_SELECT',
  'DATE',
  'NUMBER',
])

export type EditIssueFieldsSectionProps = {
  onUpdate: () => void
  issue: EditIssueFieldsSectionFragment
  inProjectsPage?: boolean
  readonly: boolean
}

function FieldsSectionHeader() {
  return (
    <div className={styles.sectionHeaderWrapper}>
      <Heading as="h3" className={styles.compactSectionHeader}>
        Fields
      </Heading>
    </div>
  )
}

export function EditIssueFieldsSection({issue: data, onUpdate, inProjectsPage, readonly}: EditIssueFieldsSectionProps) {
  const [fieldsSet, setFieldsSet] = useState<string[]>([])
  const [mutationErrorPresent, setMutationErrorPresent] = useState<boolean>(false)
  const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(false)

  const multiSelectEnabled = isFeatureEnabled('issue_fields_multi_select')

  const {
    repository: {isOwnerEnterpriseManaged, visibility},
    viewerCanSetFields,
    issueType,
  } = data

  const issueFieldsEnabled = data.repository.owner.issueFieldsEnabled ?? false
  const ownerHasIssueFields = (data.repository.owner.issueFields?.totalCount ?? 0) > 0
  const showSetupFields =
    !ownerHasIssueFields &&
    data.repository.owner.__typename === 'User' &&
    data.repository.owner.login === getCurrentUserLogin()

  const ownerPinnedFields: readonly IssueFieldSelection[] | null = useMemo(() => {
    return parsePinnedIssueFields(data.repository.owner.pinnedIssueFields?.nodes ?? [])
  }, [data.repository.owner.pinnedIssueFields])

  // Use issue type pinned fields if a type is selected, otherwise owner pinned fields
  const mergedPinnedFields: readonly IssueFieldSelection[] = useMemo(
    () =>
      issueType
        ? (parsePinnedIssueFields(
            issueType.pinnedFields?.filter(f => multiSelectEnabled || f?.dataType !== 'MULTI_SELECT'),
          ) ?? [])
        : (ownerPinnedFields ?? []),
    [issueType, ownerPinnedFields, multiSelectEnabled],
  )

  const pinnedFields = useMemo(() => {
    return mergedPinnedFields.map(field => toIssueFieldWithUnsavedValue(asIssueFieldSelection(field), null))
  }, [mergedPinnedFields])

  const showVisibilityIndicators =
    shouldShowFieldVisibilityIndicators(visibility, isOwnerEnterpriseManaged) && !!viewerCanSetFields

  if (!issueFieldsEnabled) {
    return null
  }

  if (!ownerHasIssueFields) {
    // The static settings link is only shown for repositories owned by the current user.
    return showSetupFields ? (
      <Section sectionHeader={<FieldsSectionHeader />}>
        <span className={styles.compactSectionEmptyText}>
          <Link className={styles.setupFieldsLink} href="/settings/issue-fields" inline>
            {LABELS.setupFieldsPrompt.link}
          </Link>{' '}
          {LABELS.setupFieldsPrompt.end}
        </span>
      </Section>
    ) : null
  }

  return (
    <>
      <ErrorBoundary
        fallback={
          <div>
            <Tooltip text={LABELS.loadingFieldsTooltipMessage} />
            {errorDialogOpen && (
              <ErrorDialog onClose={() => setErrorDialogOpen(false)} message={LABELS.loadingFieldsErrorMessage} />
            )}
          </div>
        }
      >
        <IssueFields
          issue={data}
          fieldsSet={fieldsSet}
          setFieldsSet={setFieldsSet}
          inProjectsPage={inProjectsPage}
          pinnedFields={pinnedFields}
          readonly={readonly}
          onUpdate={onUpdate}
          showVisibilityIndicators={showVisibilityIndicators}
          isOwnerEnterpriseManaged={!!isOwnerEnterpriseManaged}
        />
      </ErrorBoundary>

      {mutationErrorPresent && (
        <ErrorDialog onClose={() => setMutationErrorPresent(false)} message={LABELS.mutationErrorMessage} />
      )}
    </>
  )
}

function IssueFields({
  issue: data,
  fieldsSet,
  setFieldsSet,
  inProjectsPage,
  onUpdate,
  pinnedFields,
  readonly,
  showVisibilityIndicators,
  isOwnerEnterpriseManaged,
}: {
  issue: FieldsSectionFieldValuesFragment
  fieldsSet: string[]
  setFieldsSet: React.Dispatch<React.SetStateAction<string[]>>
  inProjectsPage?: boolean
  pinnedFields?: IssueFieldWithUnsavedValue[]
  readonly?: boolean
  onUpdate: () => void
  showVisibilityIndicators?: boolean
  isOwnerEnterpriseManaged?: boolean
}) {
  const [errorPresent, setErrorPresent] = useState<Record<string, boolean>>({})
  const {reportUsage} = useIssueFieldsAnalytics()
  const issueFieldValueMutation = useIssueFieldValueMutation(
    data.repository.owner.login,
    data.repository.name,
    data.number,
  )
  const deleteIssueFieldValueMutation = useDeleteIssueFieldValueMutation()

  // Add beforeunload event handler for field-level errors
  useEffect(() => {
    const hasAnyErrors = Object.values(errorPresent).some(isError => isError)

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasAnyErrors) {
        event.preventDefault()
        // This message will be displayed by browsers when users try to reload the page
        event.returnValue = LABELS.reloadWarningMessage
        return event.returnValue
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [errorPresent])
  // Use issue type ID for animation instead of pinned field IDs
  // This ensures ALL pinned fields animate when issue type changes, not just newly added ones
  const issueTypeId = data.issueType?.id || null
  const {animatePinned} = usePinnedFieldsAnimation(issueTypeId)
  const multiSelectEnabled = isFeatureEnabled('issue_fields_multi_select')

  const filteredFieldValues = useMemo<SafeFieldValue[]>(() => {
    return (data.issueFieldValues?.nodes || [])
      .map(fv => fv as SafeFieldValue)
      .filter(fv => multiSelectEnabled || fv?.field?.dataType !== 'MULTI_SELECT')
  }, [data.issueFieldValues?.nodes, multiSelectEnabled])

  const [effectiveFieldValues, setEffectiveFieldValues] = useState<SafeFieldValue[]>(
    filteredFieldValues.filter(fv => fv?.field != null),
  )

  // Reset effectiveFieldValues when navigating to a different issue
  const resetEffectiveFieldValues = useEffectEvent(() => {
    const newFilteredFieldValues: SafeFieldValue[] = (data.issueFieldValues?.nodes || [])
      .map(fv => fv as SafeFieldValue)
      .filter(fv => multiSelectEnabled || fv?.field?.dataType !== 'MULTI_SELECT')
    setEffectiveFieldValues(newFilteredFieldValues.filter(fv => fv?.field != null))
  })
  // Only reset when issue ID changes, not on every field update
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    resetEffectiveFieldValues()
  }, [data.id, multiSelectEnabled])

  const handleErrors = useCallback((fieldId: string) => {
    setErrorPresent(prev => ({...prev, [fieldId]: true}))
  }, [])

  const onCommitField = useCallback(
    (fieldId: string, fieldName: string, fieldType: RelayIssueFieldDataType, value: IssueFieldSelectionValues) => {
      const issueFieldType = toIssueFieldType(fieldType)
      const selectionValue = getIssueFieldSelectionValue(issueFieldType, value)
      if (value === '' || value === null) {
        runFieldMutation({
          mutationFn: deleteIssueFieldValueMutation,
          input: {
            issueId: data.id,
            fieldId,
            fieldName,
            owner: data.repository.owner.login,
            repo: data.repository.name,
            number: data.number,
          },
          action: 'delete',
          fieldId,
          fieldType: issueFieldType,
          inProjectsPage,
          reportUsage,
          onCompleted: () => {
            setErrorPresent(prev => ({...prev, [fieldId]: false}))
            onUpdate()
          },
          onError: () => handleErrors(fieldId),
        })
      } else {
        const fieldValueExists = filteredFieldValues.some(fieldValue => fieldValue?.field?.id === fieldId)
        const optimisticFieldValue = toOptimisticIssueFieldValue(fieldId, fieldName, issueFieldType, value)
        if (!optimisticFieldValue) {
          handleErrors(fieldId)
          return
        }
        runFieldMutation({
          mutationFn: issueFieldValueMutation,
          input: {
            issueId: data.id,
            issueField: {
              fieldId,
              ...getIssueFieldValue(issueFieldType, selectionValue),
            },
            fieldValueExists,
          },
          optimisticFieldValue,
          action: fieldValueExists ? 'update' : 'create',
          fieldId,
          fieldType: issueFieldType,
          inProjectsPage,
          reportUsage,
          onCompleted: () => {
            setErrorPresent(prev => ({...prev, [fieldId]: false}))
            onUpdate()
          },
          onError: () => handleErrors(fieldId),
        })
      }
    },
    [
      data.id,
      filteredFieldValues,
      handleErrors,
      inProjectsPage,
      issueFieldValueMutation,
      deleteIssueFieldValueMutation,
      reportUsage,
      onUpdate,
      data.number,
      data.repository.name,
      data.repository.owner.login,
    ],
  )

  // Sync effectiveFieldValues when relay data (filteredFieldValues) changes.
  // Uses the React-recommended "store previous" pattern to avoid an extra render cycle
  // that useEffect would cause (rendering stale field data for one frame).
  const [prevFilteredFieldValues, setPrevFilteredFieldValues] = useState(filteredFieldValues)
  if (filteredFieldValues !== prevFilteredFieldValues) {
    setPrevFilteredFieldValues(filteredFieldValues)

    const {addedFields, updatedFields, removedFields} = getFieldChanges(effectiveFieldValues, filteredFieldValues)

    if (addedFields.length > 0 || updatedFields.length > 0 || removedFields.length > 0) {
      // We want to maintain the initial order of fields, so we iterate over effectiveFieldValues
      const updatedFieldValues = effectiveFieldValues
        .map(fieldValue => {
          const updatedField = updatedFields.find(uf => uf?.field?.id === fieldValue?.field?.id)
          if (updatedField) return updatedField

          // Handle field removals: clear value for all fields
          const isRemoved = removedFields.some(
            (removedField: (typeof filteredFieldValues)[number]) => removedField?.field?.id === fieldValue?.field?.id,
          )
          if (isRemoved) return createClearedFieldValue(fieldValue)

          return fieldValue
        })
        .filter(fv => fv !== null && fv !== undefined)

      const hasChanged =
        addedFields.length > 0 ||
        updatedFieldValues.length !== effectiveFieldValues.length ||
        updatedFieldValues.some((fv, i) => !areFieldValuesEqual(fv, effectiveFieldValues[i]))

      if (hasChanged) setEffectiveFieldValues([...updatedFieldValues, ...addedFields])
    }
  }

  // Merge pinned fields at the beginning, then existing fields
  const allFields = useMemo(() => {
    if (!pinnedFields || pinnedFields.length === 0) {
      return effectiveFieldValues
    }

    // If any of the pinned fields already exists as a user-set field with a value, we use it and its existing value, otherwise we use the default pinned field
    const newPinnedFields = pinnedFields.map(pinnedField => {
      const userSetField = effectiveFieldValues.find(fieldValue => fieldValue.field.id === pinnedField.field.id)
      return userSetField ? userSetField : pinnedField
    })

    // remove the user-set fields that are now pinned and move unpinned fields with values to the user-set list
    const pinnedIds = newPinnedFields.map(pinnedField => pinnedField.field.id)
    const userSetFields = effectiveFieldValues.filter(fv => !pinnedIds.includes(fv.field.id))

    return [...newPinnedFields, ...userSetFields]
  }, [pinnedFields, effectiveFieldValues])

  // Update fieldsSet to include all visible fields (both with values and cleared fields)
  // to prevent them from appearing in the dropdown picker
  useEffect(() => {
    const allVisibleFieldNames = allFields.map(fv => fv?.field?.name || '').filter(name => name)
    const arraysAreEqual =
      fieldsSet.length === allVisibleFieldNames.length &&
      fieldsSet.every((name, idx) => name === allVisibleFieldNames[idx])
    if (!arraysAreEqual) {
      setFieldsSet(allVisibleFieldNames)
    }
  }, [fieldsSet, allFields, setFieldsSet])

  // Get all field IDs in order for position calculation
  const allFieldIds = allFields.map(fv => fv?.field?.id).filter((id): id is string => !!id)
  // Build a Set of pinned field IDs for position calculation
  const pinnedFieldIdSet = new Set(pinnedFields?.map(pf => pf.field.id).filter((id): id is string => !!id) ?? [])

  const fieldValues = allFields.map(fieldValue => {
    if (!fieldValue || !fieldValue.field || !fieldValue.field.id) return null
    if (fieldValue.field === undefined || fieldValue.field.dataType === undefined) return null

    // Check if this field is pinned
    const isPinned = pinnedFields?.some(pf => pf.field.id === fieldValue.field.id) || false
    const readonlyField = readonly || !data.viewerCanSetFields
    const position = getPinnedFieldPosition(fieldValue.field.id, pinnedFieldIdSet, allFieldIds)

    // Show visibility indicator for public fields
    const fieldVisibility = 'visibility' in fieldValue.field ? fieldValue.field.visibility : undefined
    const shouldShowVisibilityIndicator = showVisibilityIndicators && fieldVisibility === 'ALL'

    return getIssueFieldEditor({
      errorPresent,
      setErrorPresent: (fieldId: string, present: boolean) => setErrorPresent(prev => ({...prev, [fieldId]: present})),
      // We need the casting because the field.id property can be undefined in GraphQL
      fieldValue: fieldValue as SafeFieldValue,
      readonly: readonlyField,
      onCommit: (fieldId: string, value: IssueFieldSelectionValues) => {
        const dataType = fieldValue.field.dataType
        if (!dataType || !SUPPORTED_ISSUE_FIELD_DATA_TYPES.has(dataType)) return
        onCommitField(fieldId, fieldValue.field.name ?? '', dataType, value)
      },
      className: getPinnedFieldClassName(isPinned, animatePinned, position),
      showVisibilityIndicator: shouldShowVisibilityIndicator,
      isOwnerEnterpriseManaged,
    })
  })

  const visibleFieldValues = fieldValues.filter(Boolean)

  return (
    <Section sectionHeader={<FieldsSectionHeader />}>
      {visibleFieldValues.length > 0 ? (
        <>{visibleFieldValues}</>
      ) : (
        <span className={styles.compactSectionEmptyText}>{LABELS.noPinnedFieldsMessage(data.issueType?.name)}</span>
      )}
    </Section>
  )
}
